import { Job } from 'types/types';
import { JobRequirement, SkillMatch } from './jobMatchTypes';
import { Candidate, SkillAssessment } from 'types/types';

function calculateScore(skillMatch: SkillAssessment): number {
  let score = 0;
  switch (skillMatch.evidenceStrength.toUpperCase()) {
    case 'STRONG':
      score = 100;
      break;
    case 'MODERATE':
      score = 75;
      break;
    case 'WEAK':
      score = 50;
      break;
    case 'NONE':
      score = 0;
      break;
  }

  if (
    skillMatch.evidenceStrength === 'none' &&
    skillMatch.evidenceDetails &&
    skillMatch.evidenceDetails.length > 3
  ) {
    score = Math.min(skillMatch.evidenceDetails.length * 8, 40);
  }
  return score;
}

function requirementsFromJob(job: Job): JobRequirement[] {
  const requirements: JobRequirement[] = [];
  job.requirements?.technicalSkills?.required?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Technical Skills (required)' })
  );
  job.requirements?.technicalSkills?.preferred?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Technical Skills (preferred)' })
  );
  job.requirements?.experienceRequirements?.required?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Experience (required)' })
  );
  job.requirements?.experienceRequirements?.preferred?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Experience (preferred)' })
  );
  job.requirements?.softSkills?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Soft Skills' })
  );
  job.requirements?.experience?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Experience' })
  );
  job.requirements?.education?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Education' })
  );
  job.requirements?.certifications?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Certifications' })
  );
  job.requirements?.preferredAttributes?.forEach(requirement =>
    requirements.push({ requirement, domain: 'Preferred Attributes' })
  );
  return requirements;
}

function initialSkillMatches(requirements: JobRequirement[], candidate: Candidate): SkillMatch[] {
  return requirements.map(req => ({
    skill: req.requirement,
    skillModified: req.requirement,
    candidateId: candidate.id || '',
    domain: req.domain,
    status: 'waiting' as const,
    assessment: '',
    description: '',
    evidenceFound: false,
    evidenceStrength: 'none',
    evidenceDetails: [],
    matchScore: 0,
  }));
}

function overallMatchScore(matches: SkillMatch[]): number {
  const completedMatches = matches.filter(match => match.status === 'complete');
  if (completedMatches.length === 0) {
    return 0;
  }
  const total = completedMatches.reduce((sum, match) => sum + match.matchScore, 0);
  return Math.round(total / completedMatches.length);
}

export { calculateScore, initialSkillMatches, overallMatchScore, requirementsFromJob };
